import { Can } from '@/components/can/Can';
import { Tabs } from '@/components/tabs/Tabs';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { AssignNewRoleFormContainer } from '@/features/users/roles-privilege/AssignNewRoleFormContainer';
import { useRoleType } from '@/features/users/roles-privilege/hooks/useRoleType';
import { RolesTable } from '@/features/users/roles-privilege/table';
import { useDropdown } from '@/hooks/useDropdown';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { UserCogIcon } from '@/icons';
import { createFileRoute, SearchSchemaInput, useSearch } from '@tanstack/react-router';
import { parseAsString, useQueryState } from 'nuqs';
import { Button } from 'ui/button';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['createdBy', 'createdDate', 'status', 'roleName', 'roleDescription', 'actions']),
  desc: z.boolean(),
});

const roleSearchSchema = z.object({
  sort: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    }, z.array(sortItemSchema))
    .optional(),

  createdDate: z
    .string()

    .optional(),

  q: z
    .any()
    .transform((val) => (val == null ? undefined : String(val)))
    .optional(),

  status: z
    .string()
    .transform((val) => val.split(','))
    .pipe(z.array(z.enum(['ACTIVE', 'BLOCKED'])))
    .optional(),

  page: z.coerce.number().positive().catch(0),
  fetch: z.coerce.number().positive().max(100).catch(10),
  activeTab: z.enum(['club', 'affiliate', 'admin', 'customer']).default('admin'),
});

export const Route = createFileRoute('/(app)/users/roles/')({
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => roleSearchSchema.parse(search),
});

function RouteComponent() {
  const { activeTab } = useSearch({ from: '/(app)/users/roles/' });
  const { data: roleTypes } = useDropdown('role');
  const activeRoleTypeId = roleTypes?.find(({ label }) => label.toLowerCase() === activeTab)?.value ?? '';
  const { rolesList, isLoading, meta: pagination } = useRoleType(activeTab, activeRoleTypeId);
  const {
    dialogFormStore: { onOpen },
  } = Route.useRouteContext();

  const { t } = useDynamicLocale();

  const tabItems = [
    {
      label: t('userManagement.rolesAndPrivileges.tabs.admin'),
      value: 'admin',
      content: <RolesTable data={rolesList || []} isLoading={isLoading} pagination={pagination} />,
      disabled: false,
    },
    {
      label: t('userManagement.rolesAndPrivileges.tabs.affiliate'),
      value: 'affiliate',
      content: <RolesTable data={rolesList || []} isLoading={isLoading} pagination={pagination} />,
      disabled: true,
    },
    {
      label: t('userManagement.rolesAndPrivileges.tabs.club'),
      value: 'club',
      content: <RolesTable data={rolesList || []} isLoading={isLoading} pagination={pagination} />,
      disabled: true,
    },
    {
      label: t('userManagement.rolesAndPrivileges.tabs.customer'),
      value: 'customer',
      content: <RolesTable data={rolesList || []} isLoading={isLoading} pagination={pagination} />,
      disabled: true,
    },
  ];
  const [, setActiveTab] = useQueryState('activeTab', parseAsString.withDefault('admin'));

  const onTabChange = (tab: string): void => {
    void setActiveTab(tab);
  };
  return (
    <>
      <div className=" relative">
        <Can rule={{ allOf: [PERMS.role.assignment.assign] }}>
          <Button
            className="absolute right-0 -top-[66px] border-foreground"
            variant="outline"
            onClick={() => {
              onOpen({
                modalTitle: 'rolesPrivileges.assignNewRoleModal.title',
                title: 'Assign New Role',
                okText: t('userManagement.rolesAndPrivileges.assignRole.submit'),
                size: 'lg',
                component: <AssignNewRoleFormContainer activeTab={activeTab} />,
              });
            }}
          >
            <UserCogIcon />
            <Translation id="userManagement.rolesAndPrivileges.page.assignRoles" />
          </Button>
        </Can>
      </div>
      <Tabs
        className="rounded-lg shadow bg-muted "
        listClassName="bg-muted"
        triggerClassName="bg-muted rounded-t-[8px] px-6 py-3 cursor-pointer"
        defaultActive={activeTab}
        items={tabItems}
        onChange={onTabChange}
      />
    </>
  );
}
